import { Button, Icon, JsonViewer, Link, Text, Tooltip, View } from "../base";
import type { AgentExtraction, Evaluation, JudgeRuling } from "../../redux/reducers/evaluationSlices";
import type { AgentEnvironment } from "../../redux/reducers/agentEnvironmentSlice";
import type { Environment } from "../../redux/reducers/environmentSlice";
import { EvaluatorType, type Evaluator } from "../../redux/reducers/evaluatorSlice";
import { useAuth } from "../../hooks/useAuth";

interface EvaluationDetailViewProps {
  evaluation: Evaluation;
  evaluator: Evaluator | null;
  agentEnvironmentEntities: Record<string, AgentEnvironment>;
  environmentEntities?: Record<string, Environment>;
  agentId?: string;
  showHeader?: boolean;
  onClose?: () => void;
  sessionEnvironmentId?: string | null;
  sessionOrgEnvironmentId?: string | null;
  sessionOrigin?: string;
  sessionStatus?: string;
  sessionCreatedAt?: string | Date;
}

const formatDate = (date: Date | string) => new Date(date).toLocaleString();
const formatNumber = (value?: number) => (value === undefined ? "-" : value.toLocaleString());

export const getEvaluationContent = (evaluation: Evaluation) => {
  if (evaluation.status === "pending" || evaluation.status === "error") {
    return null;
  }
  if (evaluation.evalType === EvaluatorType.JUDGE) {
    const judgeEvaluation = evaluation as JudgeRuling;
    if (!judgeEvaluation.ruling) return null;
    const mergedJson = { ruling: judgeEvaluation.ruling, successCriteria: judgeEvaluation.successCriteria };
    return (
      <JsonViewer
        className="max-w-full"
        collapsed={5}
        data={mergedJson}
      />
    );
  }

  if (evaluation.evalType === EvaluatorType.EXTRACTION) {
    const extractionEvaluation = evaluation as AgentExtraction;
    if (!extractionEvaluation.extraction) return null;
    if (typeof extractionEvaluation.extraction === "string") {
      return <Text>{extractionEvaluation.extraction}</Text>;
    }
    const mergedJson = {
      extraction: extractionEvaluation.extraction,
      schema: {
        properties: extractionEvaluation.extractionSchema?.properties,
        required: extractionEvaluation.extractionSchema?.required,
      },
    };
    return (
      <JsonViewer
        className="max-w-full"
        collapsed={3}
        data={mergedJson}
      />
    );
  }

  return null;
};

export const getExtractionStatusIcon = (evaluation: AgentExtraction) => {
  if (evaluation.status === "pending") {
    return { name: "clock", className: "text-yellow-500", title: "Pending" } as const;
  }
  if (evaluation.status === "error") {
    return { name: "info", className: "text-red-500", title: "Error" } as const;
  }
  return { name: "assessment", className: "text-white-500", title: "Complete" } as const;
};

export const getJudgeStatusIcon = (evaluation: JudgeRuling) => {
  if (evaluation.status === "pending") {
    return { name: "clock", className: "text-yellow-500", title: "Pending" } as const;
  }
  if (evaluation.status === "error") {
    return { name: "info", className: "text-red-500", title: "Error" } as const;
  }
  if (evaluation.success === true) {
    return { name: "check", className: "text-green-500", title: "Success" } as const;
  }
  if (evaluation.success === false) {
    return { name: "close", className: "text-red-500", title: "Failed" } as const;
  }

  return { name: "help", className: "text-yellow-500" } as const;
};

const getEnvironmentNameFromEntities = (entities: Record<string, AgentEnvironment>, environmentId?: string | null) => {
  if (!environmentId) {
    return "Unknown environment";
  }

  return entities[environmentId]?.name || environmentId;
};

export const EvaluationDetailView = ({
  evaluation,
  evaluator,
  agentEnvironmentEntities,
  environmentEntities,
  agentId,
  showHeader = true,
  onClose,
  sessionEnvironmentId,
  sessionOrgEnvironmentId,
  sessionOrigin,
  sessionStatus,
  sessionCreatedAt,
}: EvaluationDetailViewProps) => {
  const { isAdmin } = useAuth();
  const orgEnvId = sessionOrgEnvironmentId ?? evaluation.session?.environmentId;
  const legacyEnvId = sessionEnvironmentId ?? evaluation.session?.agentEnvironmentId;
  const envId = orgEnvId ?? legacyEnvId;
  const sessionEnvironmentName =
    orgEnvId && environmentEntities?.[orgEnvId]
      ? environmentEntities[orgEnvId].name
      : legacyEnvId
        ? getEnvironmentNameFromEntities(agentEnvironmentEntities, legacyEnvId)
        : evaluation.session?.environment || "Unknown";
  const origin = sessionOrigin ?? evaluation.session?.origin ?? "Unknown";
  const status = sessionStatus ?? evaluation.session?.status;
  const createdAt = sessionCreatedAt ?? evaluation.session?.createdAt;
  const statusIcon = evaluation.evalType === "judge" ? getJudgeStatusIcon(evaluation as JudgeRuling) : getExtractionStatusIcon(evaluation as AgentExtraction);
  const success = evaluation.evalType === "judge" && evaluation.status !== "pending" ? evaluation.success : null;
  const totalTokens =
    evaluation.status !== "pending" && (evaluation.promptTokens !== undefined || evaluation.completionTokens !== undefined)
      ? (evaluation.promptTokens ?? 0) + (evaluation.completionTokens ?? 0)
      : null;

  return (
    <View className="flex flex-col h-full w-full">
      {showHeader && (
        <View className="flex flex-col gap-1 px-4 py-4 border-b border-gray-200 dark:border-gray-700">
          <View className="flex items-center justify-between gap-3">
            <View className="flex flex-row items-center gap-2">
              <Icon
                title={statusIcon?.title}
                name={statusIcon?.name || "assessment"}
                size={24}
                className={statusIcon?.className || "text-indigo-500"}
              />
              <Text className="text-lg font-semibold text-gray-900 dark:text-white">{evaluator?.title || "Evaluation"}</Text>
            </View>
            {onClose && (
              <Button
                variant="ghost"
                onClick={onClose}
                className="!p-2"
              >
                <Icon
                  name="close"
                  size={16}
                />
              </Button>
            )}
          </View>
        </View>
      )}

      <View className="flex-1 min-h-0 p-4 space-y-4 overflow-y-auto">
        {evaluation.status === "complete" && (
          <View className="border max-h-80 overflow-hidden overflow-y-auto border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 p-4">
            {getEvaluationContent(evaluation)}
          </View>
        )}
        {/* Evaluation Overview */}
        <View className="space-y-4">
          <View className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
            <View className="grid grid-cols-4 gap-3 text-sm text-gray-700 dark:text-gray-300">
              <View className="flex flex-col">
                <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Status</Text>
                <Text className="text-sm font-medium text-gray-900 dark:text-white mt-1 capitalize">{evaluation.status}</Text>
              </View>
              <View className="flex flex-col">
                <Text className="flex flex-col text-xs uppercase text-gray-500 dark:text-gray-400">Trigger</Text>
                <Text className="text-sm font-medium text-gray-900 dark:text-white mt-1 capitalize">{evaluation.trigger}</Text>
              </View>
              <View className="flex flex-col">
                <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Type</Text>
                <Text className="text-sm font-medium text-gray-900 dark:text-white mt-1 capitalize">{evaluation.evalType}</Text>
              </View>
              <View className="flex flex-col">
                <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Result</Text>
                <Text className="text-sm font-medium text-gray-900 dark:text-white mt-1 capitalize">
                  {success != null ? (success ? "Success" : "Fail") : "-"}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <View className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
          <View className="grid grid-cols-2 gap-3 text-sm text-gray-700 dark:text-gray-300">
            <View className="flex flex-col">
              <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Triggered</Text>
              <Text className="text-sm font-medium text-gray-900 dark:text-white mt-1">{formatDate(evaluation.createdAt)}</Text>
            </View>
            <View className="flex flex-col">
              <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Completed</Text>
              <Text className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                {evaluation.status !== "pending" ? formatDate(evaluation.updatedAt) : "-"}
              </Text>
            </View>
          </View>
        </View>

        {[EvaluatorType.EXTRACTION, EvaluatorType.JUDGE].includes(evaluation.evalType) && (
          <View className="space-y-4">
            <View className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
              <View className={`grid ${evaluation.organizationSecretName ? "grid-cols-5" : "grid-cols-4"} gap-3 text-sm text-gray-700 dark:text-gray-300`}>
                <View className="flex flex-col">
                  <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Model</Text>
                  <Text className="text-sm font-medium text-gray-900 dark:text-white mt-1">{evaluation.model ?? "-"}</Text>
                </View>
                <View className="flex flex-col">
                  <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Provider</Text>
                  <Text className="text-sm font-medium text-gray-900 dark:text-white mt-1">{evaluation.apiProvider ?? "-"}</Text>
                </View>
                {evaluation.organizationSecretName && (
                  <View className="flex flex-col">
                    <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Secret Key</Text>
                    <Text className="text-sm font-medium text-gray-900 dark:text-white mt-1">{evaluation.organizationSecretName}</Text>
                  </View>
                )}
                <View className="flex flex-col">
                  <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Tokens</Text>
                  <Tooltip
                    content={
                      <View className="flex flex-col gap-1">
                        <Text className="text-xs text-gray-700 dark:text-gray-100">{`Prompt: ${formatNumber(evaluation.promptTokens)}`}</Text>
                        <Text className="text-xs text-gray-700 dark:text-gray-100">{`Completion: ${formatNumber(evaluation.completionTokens)}`}</Text>
                      </View>
                    }
                    placement="top"
                    contentClassName="w-[160px]"
                  >
                    <View className="mt-1">
                      <Text className="text-sm font-medium text-gray-900 dark:text-white">{totalTokens !== null ? totalTokens.toLocaleString() : "-"}</Text>
                    </View>
                  </Tooltip>
                </View>
                <View className="flex flex-col">
                  <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Cost</Text>
                  <Text className="text-sm font-medium text-gray-900 dark:text-white mt-1">{`$${evaluation.cost ?? 0}`}</Text>
                </View>
              </View>
            </View>
          </View>
        )}

        {/* Session Details */}
        <View className="space-y-4">
          {evaluation.sessionId && (envId || status || createdAt) ? (
            <View className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
              <View className="flex flex-row items-center justify-between">
                <Link
                  className="text-base font-semibold text-gray-900 dark:text-white"
                  to={`/agents/${agentId}/sessions?s=${evaluation.sessionId}`}
                  onClick={onClose}
                >
                  {createdAt ? "Session - " + formatDate(createdAt) : "Session"}
                </Link>
              </View>
              <View className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700 dark:text-gray-300">
                <View className="flex flex-col">
                  <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Environment</Text>
                  <Text className="mt-1 font-medium">{sessionEnvironmentName}</Text>
                </View>
                <View className="flex flex-col">
                  <Text className="text-xs uppercase text-gray-500 dark:text-gray-400">Origin</Text>
                  <Text className="mt-1 font-medium">{origin || "Unknown"}</Text>
                </View>
              </View>
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
};
